import React, { useMemo } from 'react';

import {
  ExperimentsBag,
  ISiteApis,
  useEnvironment,
  WidgetProps,
} from '@wix/yoshi-flow-editor';

import { isStudioSite } from '@/utils';
import { initialStateBuilder } from '@/store/utils';
import { usePreloadedState } from '@/__tests__/PreloadedStateContext';
import { EXPERIMENTS } from '@/constants';

import {
  OnHandlerEvent,
  OnHandlerReset,
  SiteEcomApi,
} from 'AiAssistantWidget/types';
import {
  EditorSettingsEventsProvider,
  RootPropsProvider,
  SiteNavigation,
  SiteNavigationProvider,
  WidgetSettingsProvider,
  WidgetServicesProvider,
  WidgetStoreProvider,
  HttpClientProvider,
} from 'AiAssistantWidget/Widget/contexts';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { ChatWrapper } from '../ChatWrapper';
import { OneChatChatWrapper } from '../OneChatWrapper';
import { MobileActionBarButton } from '../MobileActionBarButton';

export type ControllerProps = {
  onHandlerEvent: OnHandlerEvent;
  onHandlerReset: OnHandlerReset;
  navigation: SiteNavigation;
  initialLayoutOpen: boolean;
  instanceToken: string;
  page: ISiteApis['currentPage'];
  settings: {
    current: Record<string, any>;
    primary: Record<string, any>;
  };
  ecom: SiteEcomApi;
  avatar?: string;
  experiments?: ExperimentsBag;
  direction?: 'ltr' | 'rtl';
};

export const ControllerValidation: React.FC<WidgetProps<ControllerProps>> = ({
  onHandlerEvent,
  onHandlerReset,
  navigation,
  initialLayoutOpen,
  instanceToken,
  avatar,
  page,
  host,
  settings,
  ecom,
  experiments: experimentsBag,
  direction,
}) => {
  const { isEditor, isMobile } = useEnvironment();
  const preloadedState = usePreloadedState();

  const initialState = useMemo(
    () => ({
      ...initialStateBuilder({
        isEditor,
        isMobile,
        initialLayoutOpenProp: initialLayoutOpen,
      }),
      ...preloadedState,
    }),
    [initialLayoutOpen, isEditor, isMobile, preloadedState],
  );

  return (
    <div>
      <HttpClientProvider instance={instanceToken}>
        <WidgetServicesProvider instance={instanceToken} ecom={ecom}>
          <WidgetStoreProvider initialState={initialState}>
            <RootPropsProvider
              p={{
                page,
                instanceToken,
                host,
                avatar,
                experiments: experimentsBag,
                direction,
              }}
            >
              <WidgetSettingsProvider settings={settings}>
                <EditorSettingsEventsProvider
                  onHandlerEvent={onHandlerEvent}
                  onHandlerReset={onHandlerReset}
                >
                  <SiteNavigationProvider navigation={navigation}>
                    <ChatChooser />
                    {!isStudioSite() && <MobileActionBarButton host={host} />}
                  </SiteNavigationProvider>
                </EditorSettingsEventsProvider>
              </WidgetSettingsProvider>
            </RootPropsProvider>
          </WidgetStoreProvider>
        </WidgetServicesProvider>
      </HttpClientProvider>
    </div>
  );
};

const ChatChooser = () => {
  const { experiments } = useAppExperiments();
  const isOneChat = experiments.enabled(EXPERIMENTS.ONE_CHAT);

  return isOneChat ? <OneChatChatWrapper /> : <ChatWrapper />;
};
